// src/pages/store/listings/DesktopCreateListing.js
import React, { useState } from 'react';
import { FiArrowLeft, FiChevronRight } from 'react-icons/fi';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Button from '../../../components/common/Button';
import ImagesForm from '../../../components/store/listing/ImagesForm';
import SpecializedForm from '../../../components/store/listing/specialized/SpecializedForm';
import CategoryModal from './CategoryModal';
import LocationModal from './LocationModal';
import { MdOutlineToggleOn, MdOutlineToggleOff } from 'react-icons/md';

const DesktopCreateListing = ({
  form,
  errors,
  submitting,
  images,
  imageUploadProgress,
  allCategories,
  mainCategories,
  subcategories,
  categoryType,
  shouldShowCondition,
  states,
  lgas,
  lcdas,
  showLcdaField,
  loadingStates,
  loadingLgas,
  loadingLcdas,
  onBack,
  onSubmit,
  onFieldChange,
  onImagesChange,
  onMainCategorySelect,
  onSubcategorySelect,
  onStateSelect,
  onLgaSelect,
  onLcdaSelect,
  onImageUploadProgress,
}) => {
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [locationModalVisible, setLocationModalVisible] = useState(false);

  const getSelectedCategoryName = () => {
    if (!form.subcategory_id && !form.category_id) return 'Select category';
    if (form.subcategory_id) {
      const subcategory = allCategories.find(cat => cat.id === parseInt(form.subcategory_id));
      const mainCategory = allCategories.find(cat => cat.id === parseInt(form.category_id));
      return `${mainCategory?.name} › ${subcategory?.name}`;
    }
    const mainCategory = allCategories.find(cat => cat.id === parseInt(form.category_id));
    return mainCategory?.name || 'Select category';
  };

  const formatNumber = (value) => {
    if (!value) return '';
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  
  const unformatNumber = (value) => value.replace(/,/g, '');
  

  const getSelectedLocation = () => {
    if (!form.state_id) return 'Select location';
    const state = states.find(s => parseInt(s.value) === parseInt(form.state_id));
    const lga = lgas.find(l => parseInt(l.value) === parseInt(form.lga_id));
    const lcda = lcdas.find(l => parseInt(l.value) === parseInt(form.lcda_id));
    
    let location = state?.label || '';
    if (lga) location += ` › ${lga.label}`;
    if (lcda) location += ` › ${lcda.label}`;
    return location;
  };

  return (
    <div className="hidden lg:block min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            disabled={submitting}
          >
            <FiArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Create New Listing</h1>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center space-x-8">
            {['Basic Info', 'Details', 'Photos', 'Review'].map((step, index) => (
              <div key={step} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    index === 0 ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {index + 1}
                  </div>
                  <span className={`mt-2 text-sm ${
                    index === 0 ? 'text-orange-600 font-semibold' : 'text-gray-500'
                  }`}>
                    {step}
                  </span>
                </div>
                {index < 3 && <div className="w-16 h-0.5 bg-gray-300 mx-4"></div>}
              </div>
            ))}
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Title *</h3>
                <input
                  type="text"
                  placeholder="What are you selling?"
                  value={form.title}
                  onChange={(e) => onFieldChange('title', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg ${errors.title ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.title && <p className="mt-2 text-sm text-red-600">{errors.title}</p>}
              </div>
            </div>

            {/* Category */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Category *</h3>
                <button
                  onClick={() => setCategoryModalVisible(true)}
                  className={`w-full flex items-center justify-between p-4 border rounded-lg ${errors.category ? 'border-red-300' : 'border-gray-300'}`}
                >
                  <span className={form.category_id ? "text-gray-900" : "text-gray-500"}>
                    {getSelectedCategoryName()}
                  </span>
                  <FiChevronRight className="w-5 h-5 text-gray-400" />
                </button>
                {errors.category && <p className="mt-2 text-sm text-red-600">{errors.category}</p>}
              </div>
            </div>

            {/* Images */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <ImagesForm 
                images={images}
                errors={errors}
                submitting={submitting}
                onImagesChange={onImagesChange}
                onImageUploadProgress={onImageUploadProgress}
                imageUploadProgress={imageUploadProgress}
              />
            </div>

            {/* Location */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Location *</h3>
                <button
                  onClick={() => setLocationModalVisible(true)}
                  className={`w-full flex items-center justify-between p-4 border rounded-lg ${errors.location ? 'border-red-300' : 'border-gray-300'}`}
                >
                  <span className={form.state_id ? "text-gray-900" : "text-gray-500"}>
                    {getSelectedLocation()}
                  </span>
                  <FiChevronRight className="w-5 h-5 text-gray-400" />
                </button>
                {errors.location && <p className="mt-2 text-sm text-red-600">{errors.location}</p>}
              </div>
            </div>

            {/* Specialized Form - Now using form prop directly */}
            {categoryType && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <SpecializedForm 
                  categoryType={categoryType}
                  form={form}
                  errors={errors}
                  submitting={submitting}
                  onFieldChange={onFieldChange}
                  categoryId={form.category_id}
                />
              </div>
            )}

            {/* Description & Price */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {categoryType === 'services' ? 'Service Description' : 'Description'}
                  </h3>
                  <textarea
                    placeholder={categoryType === 'services' 
                      ? "Describe your service in detail..." 
                      : "Describe your item in detail..."
                    }
                    value={form.description}
                    onChange={(e) => onFieldChange('description', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {categoryType === 'services' ? 'Starting Price *' : 'Price *'}
                    </h3>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">₦</span>
                      </div>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="0.00"
                        value={formatNumber(form.price)}
                        onChange={(e) => {
                          const rawValue = unformatNumber(e.target.value);

                          // allow only digits
                          if (!/^\d*$/.test(rawValue)) return;

                          onFieldChange('price', rawValue);
                        }}
                        className={`w-full pl-8 pr-4 py-3 border rounded-lg
                          ${errors.price ? 'border-red-400' : 'border-gray-300'}
                        `}
                      />

                    </div>
                    {errors.price && <p className="mt-2 text-sm text-red-600">{errors.price}</p>}
                  </div>

                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => onFieldChange('is_negotiable', !form.is_negotiable)}
                      className={`flex items-center gap-2 p-4 border rounded-lg transition ${
                        form.is_negotiable 
                          ? 'bg-green-50 border-green-500 text-green-700' 
                          : 'bg-gray-50 border-gray-300 text-gray-700'
                      }`}
                    >
                      {form.is_negotiable ? (
                        <MdOutlineToggleOn className="w-6 h-6 text-green-500" />
                      ) : (
                        <MdOutlineToggleOff className="w-6 h-6 text-gray-400" />
                      )}
                      <span className="text-sm font-medium">Negotiable</span>
                    </button>
                  </div>
                </div>
                
                {/* Condition Dropdown (for non-services) */}
                {categoryType !== 'services' && shouldShowCondition() && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Condition
                    </label>
                    <select
                      value={form.condition || 'brand_new'}
                      onChange={(e) => onFieldChange('condition', e.target.value)}
                      disabled={submitting}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white"
                    >
                      <option value="brand_new">Brand New</option>
                      <option value="like_new">Like New</option>
                      <option value="fairly_used">Fairly Used</option>
                      <option value="used">Used</option>
                      <option value="refurbished">Refurbished</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
            
            {/* Submit Button */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <Button
                children={submitting ? "Creating..." : "Create Listing"}
                onClick={onSubmit}
                disabled={submitting}
                loading={submitting}
                variant="primary"
                className="w-full"
              />
              <p className="mt-4 text-sm text-gray-500 text-center">
                Complete all required fields marked with *
              </p>
            </div>
          </div>

          {/* Right Column - Tips */}
          <div className="space-y-6">
            {/* Tips */}
            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-100">
              <h4 className="font-semibold text-gray-900 mb-3">💡 Tips for Better Listings</h4>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs">📸</span>
                  </div>
                  <span>Use clear, well-lit photos</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs">💰</span>
                  </div>
                  <span>Set a competitive price</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs">✍️</span>
                  </div>
                  <span>Write detailed descriptions</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs">⚡</span>
                  </div>
                  <span>Respond quickly to messages</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {errors.general && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{errors.general}</p>
          </div>
        )}
      </main>

      {/* Modals */}
      {categoryModalVisible && (
        <CategoryModal
          form={form}
          mainCategories={mainCategories}
          subcategories={subcategories}
          allCategories={allCategories}
          onMainCategorySelect={onMainCategorySelect}
          onSubcategorySelect={(id) => {
            onSubcategorySelect(id);
            setCategoryModalVisible(false);
          }}
          onClose={() => setCategoryModalVisible(false)}
          isMobile={false}
        />
      )}

      {locationModalVisible && (
        <LocationModal
          form={form}
          states={states}
          lgas={lgas}
          lcdas={lcdas}
          showLcdaField={showLcdaField}
          loadingStates={loadingStates}
          loadingLgas={loadingLgas}
          loadingLcdas={loadingLcdas}
          onStateSelect={onStateSelect}
          onLgaSelect={onLgaSelect}
          onLcdaSelect={(id) => {
            onLcdaSelect(id);
            setLocationModalVisible(false);
          }}
          onClose={() => setLocationModalVisible(false)}
          isMobile={false}
        />
      )}

      <Footer />
    </div>
  );
};

export default DesktopCreateListing;